import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EventBus } from '../../../shared/EventBus';
import type { EventPayloadMap } from '../../../shared/EventBus/EventPayloadMap';
import { KeyBindingManager } from '../../services/KeyBindingManager';
import { contextualBindingLabel, matchesContextualBinding } from '../../systems/input/contextualBindings';

export type ContextualInteractionData = EventPayloadMap['ui:contextualInteraction'];

export const ContextualInteractionPrompt: React.FC = () => {
  const [interaction, setInteraction] = useState<ContextualInteractionData>(null);
  const [, refreshBindings] = useState(0);
  const control = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => KeyBindingManager.getInstance().subscribe(() => refreshBindings(value => value + 1)), []);
  useLayoutEffect(() => {
    const node = control.current;
    return () => {
      const previous = previousFocus.current;
      if (previous?.isConnected && (document.activeElement === node || document.activeElement === document.body)) {
        previous.focus();
      }
      previousFocus.current = null;
    };
  }, [interaction?.id]);

  useEffect(() => {
    const unsub = EventBus.getInstance().on('ui:contextualInteraction', (payload) => {
      setInteraction(payload);
    });
    return () => {
      unsub();
    };
  }, []);

  if (!interaction) return null;
  if (typeof document === 'undefined') return null;

  const handleActivate = () => {
    // Deliberately trigger interaction for the contextual target
    EventBus.getInstance().emit('ui:contextualInteraction:activate', { id: interaction.id });
  };

  return createPortal(
    <div
      ref={control}
      data-testid="contextual-interaction-prompt"
      role="button"
      tabIndex={0}
      aria-label={`${interaction.name} ${interaction.action}`}
      className="contextual-interaction-prompt"
      style={{
        position: 'fixed',
        bottom: '180px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(15, 23, 42, 0.9)',
        border: '1px solid #38bdf8',
        borderRadius: '8px',
        padding: '8px 16px',
        color: '#f8fafc',
        fontFamily: 'monospace',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        zIndex: 100000,
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        userSelect: 'none',
        pointerEvents: 'auto',
      }}
      onClick={handleActivate}
      onFocus={(event) => {
        if (event.relatedTarget instanceof HTMLElement) previousFocus.current = event.relatedTarget;
      }}
      onKeyDown={(e) => {
        const standard = !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey &&
          (e.key === 'Enter' || e.key === ' ');
        if (standard || matchesContextualBinding(e.nativeEvent, KeyBindingManager.getInstance().getBindings())) {
          e.preventDefault();
          e.stopPropagation();
          if (!e.repeat) handleActivate();
        }
      }}
    >
      <span style={{ background: '#0284c7', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
        {contextualBindingLabel(KeyBindingManager.getInstance().getBindings())}
      </span>
      <span>{interaction.action}</span>
      <span style={{ opacity: 0.8, fontSize: '12px' }}>({interaction.name})</span>
    </div>,
    document.body,
  );
};
